import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { GoogleReview } from '@app/@shared/model/google-reviews.model';
import { SERVER_API_URL } from '@app/app.constants';
import { catchError, map, Observable, of } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class GoogleReviewsCommentsService {
  constructor(private http: HttpClient) {}

  getReviewsComments(): Observable<GoogleReview[]> {
    const ratingMap: { [key: string]: number } = {
      ONE: 1,
      TWO: 2,
      THREE: 3,
      FOUR: 4,
      FIVE: 5,
    };
    return this.http
      .get<GoogleReview[]>(SERVER_API_URL + `google/reviews`)
      .pipe(
        map((response: GoogleReview[]) => {
          return response.map((review) => ({
            ...review,
            profileName: this.capitalizeWords(review.reviewer.displayName),
            rating: ratingMap[review.starRating] || 0,
            profilePhotoUrl: review.reviewer.profilePhotoUrl,
            description: this.extractSpanishDescription(review.comment),
            description_en: this.extractEnglishDescription(review.comment),
          }));
        }),
        catchError((error) => {
          return of(error);
        })
      );
  }

  private extractEnglishDescription(comment: string): string {
    if (!comment) return '';

    // Pattern to match "(Translated by Google) [English text]\n\n(Original)\n[Spanish text]"
    const translatedPattern =
      /\(Translated by Google\)\s*(.*?)\s*\n\n\(Original\)/s;
    const match = comment.match(translatedPattern);

    if (match && match[1]) {
      return match[1].trim();
    }

    // If no translation pattern found, assume the comment is already in the original language
    return '';
  }

  private extractSpanishDescription(comment: string): string {
    if (!comment) return '';

    // Pattern to match the original text after "(Original)"
    const originalPattern = /\(Original\)\s*(.*?)$/s;
    const match = comment.match(originalPattern);

    if (match && match[1]) {
      return match[1].trim();
    }

    // If no translation pattern found, assume the comment is the original Spanish text
    return comment.trim();
  }

  /**
   * Capitalizes the first letter of each word and makes the rest lowercase.
   * Returns an empty string if input is falsy.
   */
  private capitalizeWords(value: string | undefined | null): string {
    if (!value) return '';
    return value
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }
}
